import React, { FC, useState, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';

import { CentreHeading } from '../common-styles/common-styles';
import { BaseTournamentData, useTournamentMutation } from '../../api/tournament-data';
import { SelectedIndexContext } from '../../App';
import { RYDER_CUP_TYPE, STABLEFORD_WITH_TEAM_GAMES, TOURNAMENT_STATE_IN_PLAY } from '../../app/constants';
import { getTourGroup } from '../../utils/get-tour-group';
import { TournamentConfig } from '../../../database.schema';

export const FlexColumnLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  margin: 1rem 0.25rem;
  align-items: flex-start;
`;
const PageWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 2rem 1rem;
  flex-direction: column;
`;
const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem;
`;
const Button = styled.button`
  border-radius: 5px;
  width: 4rem;
  height: 1.5rem;
  background: lightblue;
  border: 1px solid #5c9db2;
  font-weight: 500;
  color: white;
  margin: 1rem 1rem;
  &:disabled {
    background-color: lightgray;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const ErrorWrapper = styled.div`
  color: red;
`;
export const MatchEntryInput = styled.input`
  margin: 0 0.5rem;
`;
export const MatchEntryLabel = styled.label`
  margin-right: 0.5rem;
`;

const getBaseTournamentData = (type: string, selectedTournamentIndex: number, year: string, config: TournamentConfig): BaseTournamentData => {
  if (type === RYDER_CUP_TYPE) {
    return {
      type,
      selectedTournamentIndex: selectedTournamentIndex + 1,
      state: TOURNAMENT_STATE_IN_PLAY,
      year: Number(year),
      config,
    };
  }
  return {
    type,
    selectedTournamentIndex: selectedTournamentIndex + 1,
    state: TOURNAMENT_STATE_IN_PLAY,
    year: Number(year),
    config,
  };
};

export const CreateTournament: FC = () => {
  const { selectedTournamentIndex, setSelectedTournamentIndex } = useContext(SelectedIndexContext);
  const params = useParams();
  const navigate = useNavigate();
  const mutation = useTournamentMutation();
  const [tournamentType, setTournamentType] = useState<string>('');
  const [showWonHalvedLost, setShowWonHalvedLost] = useState<boolean>(true);
  const [showCaptainsRace, setShowCaptainsRace] = useState<boolean>(true);
  const [showLongestDrive, setShowLongestDrive] = useState<boolean>(true);
  const [showNearestThePin, setShowNearestThePin] = useState<boolean>(true);
  const [showNearestThePinIn2, setShowNearestThePinIn2] = useState<boolean>(true);
  const year = params.year ?? new Date().getFullYear().toString();

  const createNewTournament = () => {
    const config = {
      showWonHalvedLost,
      showCaptainsRace,
      showLongestDrive,
      showNearestThePin,
      showNearestThePinIn2,
    };
    const dataToSave = getBaseTournamentData(tournamentType, selectedTournamentIndex, year, config);
    if (dataToSave) {
      mutation.mutate(
        { ...dataToSave },
        {
          onSuccess: (data) => {
            setSelectedTournamentIndex(selectedTournamentIndex + 1);
            navigate('/');
          },
          onError: (error) => {
            console.log('<><><>ERROR<><><><', error);
          },
        },
      );
      navigate(`/`);
    }
  };

  return (
    <PageWrapper>
      <CentreHeading>
        <h2>Create New {`${getTourGroup().toLocaleUpperCase()}`} Tournament</h2>
      </CentreHeading>
      <FlexColumnLeft>
        <h3>Tournament settings</h3>
        <InputWrapper>
          <MatchEntryInput
            type="checkbox"
            name="showWonHalvedLost"
            id="showWonHalvedLost"
            data-testid="tier-points-type"
            checked={showWonHalvedLost}
            onChange={() => setShowWonHalvedLost(!showWonHalvedLost)}
          />
          <MatchEntryLabel htmlFor="showWonHalvedLlost">Show Won, Halved & Lost totals</MatchEntryLabel>
        </InputWrapper>
        <InputWrapper>
          <MatchEntryInput
            type="checkbox"
            name="showCaptainsRace"
            id="showCaptainsRace"
            data-testid="show-captains-race"
            checked={showCaptainsRace}
            onChange={() => setShowCaptainsRace(!showCaptainsRace)}
          />
          <MatchEntryLabel htmlFor="showWonHalvedLlost">Show Captains Race</MatchEntryLabel>
        </InputWrapper>
        <InputWrapper>
          <MatchEntryInput
            type="checkbox"
            name="showLongestDrive"
            id="showLongestDrive"
            data-testid="show-longest-drive"
            checked={showLongestDrive}
            onChange={() => setShowLongestDrive(!showLongestDrive)}
          />
          <MatchEntryLabel htmlFor="showWonHalvedLlost">Show Longest Drive</MatchEntryLabel>
        </InputWrapper>
        <InputWrapper>
          <MatchEntryInput
            type="checkbox"
            name="showNearestThePin"
            id="showNearestThePin"
            data-testid="show-nearest-the-pin"
            checked={showNearestThePin}
            onChange={() => setShowNearestThePin(!showNearestThePin)}
          />
          <MatchEntryLabel htmlFor="showWonHalvedLlost">Show Nearest The Pin</MatchEntryLabel>
        </InputWrapper>
        <InputWrapper>
          <MatchEntryInput
            type="checkbox"
            name="showNearestThePinIn2"
            id="showNearestThePinIn2"
            data-testid="show-nearest-the-pin-in-2"
            checked={showNearestThePinIn2}
            onChange={() => setShowNearestThePinIn2(!showNearestThePinIn2)}
          />
          <MatchEntryLabel htmlFor="showWonHalvedLlost">Show Nearest The Pin In 2</MatchEntryLabel>
        </InputWrapper>
        <h3>Tournament type</h3>
        <InputWrapper>
          <MatchEntryInput
            type="radio"
            name="tournamentType"
            value={RYDER_CUP_TYPE}
            id="ryder-up"
            data-testid="ryder-cup-type"
            checked={tournamentType === RYDER_CUP_TYPE}
            onChange={() => {
              setTournamentType(RYDER_CUP_TYPE);
              setShowWonHalvedLost(true);
            }}
          />
          <MatchEntryLabel htmlFor="ryder-cup">Ryder Cup</MatchEntryLabel>
        </InputWrapper>
        <InputWrapper>
          <MatchEntryInput
            type="radio"
            name="tournamentType"
            value={STABLEFORD_WITH_TEAM_GAMES}
            id="tier-points"
            data-testid="tier-points-type"
            checked={tournamentType === STABLEFORD_WITH_TEAM_GAMES}
            onChange={() => setTournamentType(STABLEFORD_WITH_TEAM_GAMES)}
          />
          <MatchEntryLabel htmlFor="tier-points">Stabledford With Tier Points</MatchEntryLabel>
        </InputWrapper>
        <ButtonWrapper>
          <Button data-testid="submit-button" onClick={() => createNewTournament()} disabled={tournamentType === ''}>
            Submit
          </Button>
        </ButtonWrapper>
      </FlexColumnLeft>
    </PageWrapper>
  );
};
